import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LOGO_URL } from "@/lib/useReveal";
import { CEO_PORTRAIT, EVOLVE, READY_STATS } from "@/demo/evolveData";
import { startDemo } from "@/demo/demoApi";

const BOOT = [
    "Connecting to EVOLVE…", "Initializing CEO GPT…", "Loading Company Profile…",
    "Loading 10 Department Agents…", "Loading Sales Intelligence…", "Loading Finance Intelligence…",
    "Loading HR Intelligence…", "Loading Operations…", "Loading Marketing…",
    "Loading Customer Support…", "Loading Procurement…", "Loading Legal…", "Loading Strategy…",
    "Loading Meeting Intelligence…", "Reading 2,400 Business Records…", "Reading 650 Documents…",
    "Generating Executive Context…", "Building Decision Graph…", "Preparing 24 Executive Decision Cards…",
    "Optimizing Business Memory…", "Almost Ready…", "Welcome CEO.",
];
const TIPS = [
    "You can ask questions naturally.",
    "CEO GPT automatically combines information across departments.",
    "Every important answer becomes a Decision Card.",
    "Approvals happen directly inside chat.",
    "No dashboards required — your company becomes conversational.",
];

function Particles() {
    const dots = Array.from({ length: 26 });
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {dots.map((_, i) => {
                const s = 2 + (i % 4);
                return (
                    <span key={i} className="absolute rounded-full bg-violet-400/40 anim-float-slow"
                        style={{
                            width: s, height: s, left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`,
                            animationDelay: `${(i % 7) * 0.4}s`, animationDuration: `${6 + (i % 5)}s`,
                        }} />
                );
            })}
        </div>
    );
}

export default function DemoIntro() {
    const navigate = useNavigate();
    const [step, setStep] = useState("welcome"); // welcome | loading | ready
    const [name, setName] = useState("");
    const [progress, setProgress] = useState(0);
    const [bootIdx, setBootIdx] = useState(0);
    const [tipIdx, setTipIdx] = useState(0);
    const started = useRef(false);

    function begin(e) {
        e.preventDefault();
        const nm = name.trim() || "Alex";
        setName(nm);
        sessionStorage.setItem("evolve_demo_name", nm);
        setStep("loading");
        startDemo().then((d) => sessionStorage.setItem("evolve_demo_session", d.session_id)).catch(() => {});
    }

    useEffect(() => {
        if (step !== "loading" || started.current) return;
        started.current = true;
        const total = 9500;
        const t0 = Date.now();
        const iv = setInterval(() => {
            const p = Math.min(100, Math.round(((Date.now() - t0) / total) * 100));
            setProgress(p);
            setBootIdx(Math.min(BOOT.length - 1, Math.floor((p / 100) * BOOT.length)));
            if (p >= 100) { clearInterval(iv); setTimeout(() => setStep("ready"), 500); }
        }, 90);
        const tv = setInterval(() => setTipIdx((i) => (i + 1) % TIPS.length), 2200);
        return () => { clearInterval(iv); clearInterval(tv); };
    }, [step]);

    const R = 118, C = 2 * Math.PI * R;

    return (
        <div data-testid="demo-intro" className="fixed inset-0 z-[200] overflow-hidden bg-[#0b0b12] text-white">
            {/* backdrop gradients */}
            <div className="absolute inset-0" style={{ background: "radial-gradient(60% 55% at 50% 35%, rgba(139,92,246,0.28), transparent 70%), radial-gradient(50% 50% at 80% 90%, rgba(99,102,241,0.18), transparent 70%)" }} />
            <Particles />

            <AnimatePresence mode="wait">
                {step === "welcome" && (
                    <motion.div key="welcome" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                        className="relative h-full flex items-center justify-center p-5">
                        <div className="w-full max-w-md rounded-[28px] border border-white/10 bg-white/[0.06] backdrop-blur-2xl p-8 text-center shadow-[0_40px_120px_-30px_rgba(139,92,246,0.5)]">
                            <button onClick={() => navigate("/")} data-testid="demo-close"
                                className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full hover:bg-white/10 text-white/70">✕</button>
                            <div className="mx-auto relative h-24 w-24">
                                <div className="absolute inset-0 rounded-full anim-pulse-glow" style={{ background: "radial-gradient(closest-side, rgba(139,92,246,0.6), transparent 70%)" }} />
                                <img src={CEO_PORTRAIT} alt="CEO" className="relative h-24 w-24 rounded-full object-cover ring-2 ring-white/20" />
                            </div>
                            <div className="mt-5 text-[11px] font-semibold tracking-[0.24em] uppercase text-violet-300">Welcome Future CEO</div>
                            <h2 className="mt-2 font-display text-3xl font-light leading-tight">
                                Today you become the CEO of<br /><span className="italic font-normal text-violet-200">{EVOLVE.name}</span>
                            </h2>
                            <p className="mt-3 text-[13.5px] text-white/70 leading-relaxed">
                                Experience how CEO's GPT turns conversations into executive decisions — no signup, no dashboards.
                            </p>
                            <form onSubmit={begin} className="mt-6 text-left">
                                <label className="text-[11px] font-semibold tracking-[0.16em] uppercase text-white/50">What should we call you?</label>
                                <input data-testid="demo-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
                                    placeholder="John Smith"
                                    className="mt-2 w-full rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-violet-400/60" />
                                <button data-testid="demo-start-btn" type="submit"
                                    className="mt-4 w-full rounded-2xl bg-white text-black font-medium py-3 text-[15px] hover:shadow-[0_20px_50px_-15px_rgba(139,92,246,0.8)] transition-all">
                                    Start Demo →
                                </button>
                            </form>
                            <div className="mt-4 text-[11px] text-white/40">Demo Mode · Mock Company · No real actions executed</div>
                        </div>
                    </motion.div>
                )}

                {step === "loading" && (
                    <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-white text-neutral-900 overflow-hidden" data-testid="demo-loading">
                        {/* bright purple VFX */}
                        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(60% 55% at 50% 35%, rgba(139,92,246,0.16), transparent 70%), radial-gradient(50% 50% at 80% 90%, rgba(99,102,241,0.12), transparent 70%)" }} />
                        <Particles />
                        <div className="relative h-[280px] w-[280px]">
                            <svg className="absolute inset-0 -rotate-90" width="280" height="280">
                                <circle cx="140" cy="140" r={R} stroke="rgba(139,92,246,0.12)" strokeWidth="8" fill="none" />
                                <circle cx="140" cy="140" r={R} stroke="url(#g)" strokeWidth="8" fill="none" strokeLinecap="round"
                                    strokeDasharray={C} strokeDashoffset={C - (progress / 100) * C} style={{ transition: "stroke-dashoffset 0.15s linear" }} />
                                <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#a78bfa" /><stop offset="100%" stopColor="#6366f1" /></linearGradient></defs>
                            </svg>
                            <div className="absolute inset-0 grid place-items-center">
                                <img src={LOGO_URL} alt="" className="logo-blend h-32 w-32 object-contain anim-float-slow" style={{ filter: "drop-shadow(0 16px 40px rgba(139,92,246,0.4))" }} />
                            </div>
                            <div className="absolute -bottom-4 inset-x-0 text-center font-display text-3xl text-neutral-900">{progress}%</div>
                        </div>
                        <div data-testid="demo-boot-msg" className="relative mt-16 h-6 text-[15px] font-semibold text-violet-600">{BOOT[bootIdx]}</div>
                        <AnimatePresence mode="wait">
                            <motion.div key={tipIdx} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                                className="relative mt-3 text-[13px] text-neutral-500 max-w-sm text-center">Tip · {TIPS[tipIdx]}</motion.div>
                        </AnimatePresence>
                    </motion.div>
                )}

                {step === "ready" && (
                    <motion.div key="ready" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
                        className="relative h-full flex items-center justify-center p-6" data-testid="demo-ready">
                        <div className="w-full max-w-lg rounded-[28px] border border-white/10 bg-white/[0.06] backdrop-blur-2xl p-8 text-center shadow-[0_40px_120px_-30px_rgba(139,92,246,0.5)]">
                            <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-emerald-300">EVOLVE is Ready</div>
                            <h2 className="mt-2 font-display text-3xl font-light">Welcome, <span className="italic font-normal text-violet-200">{name}</span></h2>
                            <p className="mt-2 text-[13.5px] text-white/70">You are now the CEO of {EVOLVE.name}.</p>
                            <div className="mt-6 grid grid-cols-3 gap-3">
                                {READY_STATS.map(([v, k]) => (
                                    <div key={k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
                                        <div className="font-display text-xl text-violet-200">{v}</div>
                                        <div className="text-[10px] tracking-[0.1em] uppercase text-white/45 mt-0.5">{k}</div>
                                    </div>
                                ))}
                            </div>
                            <button data-testid="demo-enter-btn" onClick={() => navigate("/app/chat-demo")}
                                className="mt-7 w-full rounded-2xl bg-white text-black font-medium py-3.5 text-[15px] hover:shadow-[0_20px_50px_-15px_rgba(139,92,246,0.8)] transition-all">
                                Enter CEO GPT →
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
